"use client";

import React from "react";

import {
  Box,
  Button,
  FormGroup,
  InputAdornment,
  TextField,
} from "@mui/material";
import Kicker from "@/src/components/common/broadsheet/kicker";
import VISUALLY_HIDDEN from "@/src/utils/visually-hidden";
import { fieldSx } from "@/src/components/common/broadsheet/form-kit";
import {
  FONT_TEXT,
  FONT_UI,
  hairline,
} from "@/src/components/common/broadsheet/tokens";
import dynamic from "next/dynamic";

const SEARCH_FIELDS = [
  { key: "title", label: "Title", description: "Search in article titles" },
  {
    key: "keywords",
    label: "Keywords",
    description: "Search in article keywords",
  },
  { key: "authors", label: "Authors", description: "Search in author names" },
  {
    key: "abstract",
    label: "Abstract",
    description: "Search in article abstracts",
  },
  { key: "body", label: "Body", description: "Search in full article text" },
  {
    key: "references",
    label: "References",
    description: "Search in article references",
  },
  { key: "doi", label: "DOI", description: "Search by DOI identifier" },
] as const;

type SearchFieldKey = (typeof SEARCH_FIELDS)[number]["key"];

const pillSx = (active: boolean) => ({
  display: "inline-flex",
  alignItems: "center",
  cursor: "pointer",
  userSelect: "none" as const,
  px: 0.75,
  py: 0.3125,
  borderRadius: "999px",
  border: "1px solid",
  fontFamily: FONT_UI,
  fontSize: "0.8125rem",
  fontWeight: 600,
  lineHeight: 1.2,
  transition: "background-color .15s, border-color .15s, color .15s",
  ...(active
    ? {
        backgroundColor: "primary.main",
        borderColor: "primary.main",
        color: "primary.contrastText",
      }
    : {
        backgroundColor: "custom.newsprintRaised",
        borderColor: "custom.ruleHairline",
        color: "custom.ink",
        "&:hover": { borderColor: "primary.main", color: "primary.main" },
      }),
  "&:focus-within": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: "2px",
  },
});

interface SearchBarProps {
  didYouMean: any;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchFields: Record<SearchFieldKey, boolean>;
  setSearchFields: React.Dispatch<
    React.SetStateAction<Record<SearchFieldKey, boolean>>
  >;
}

const SearchBarMain = ({
  didYouMean = [],
  searchQuery,
  setSearchQuery,
  searchFields,
  setSearchFields,
}: SearchBarProps) => {
  const generateSuggestions = () => {
    if (!didYouMean || !Array.isArray(didYouMean) || didYouMean.length === 0) {
      return [];
    }

    if (
      !searchQuery ||
      typeof searchQuery !== "string" ||
      searchQuery.trim() === ""
    ) {
      return [];
    }

    const MAX_QUERY_LENGTH = 1000;
    const MAX_SUGGESTIONS = 5;
    const MAX_WORD_LENGTH = 100;

    if (searchQuery.length > MAX_QUERY_LENGTH) {
      console.warn("Search query too long, skipping suggestions");
      return [];
    }

    const suggestions: string[] = [];
    let originalQuery = searchQuery.toLowerCase().trim();
    const replacements: Array<{ original: string; options: string[] }> = [];

    didYouMean.forEach((item) => {
      if (!item || typeof item !== "object") return;
      if (!item.text || typeof item.text !== "string") return;
      if (
        !item.options ||
        !Array.isArray(item.options) ||
        item.options.length === 0
      )
        return;

      if (item.text.length > MAX_WORD_LENGTH) {
        console.warn(
          "Word too long, skipping:",
          item.text.substring(0, 50) + "...",
        );
        return;
      }

      const validOptions = item.options.filter(
        (option: string) =>
          option &&
          typeof option === "string" &&
          option.trim() !== "" &&
          option.toLowerCase() !== "undefined" &&
          option.length <= MAX_WORD_LENGTH,
      );

      if (validOptions.length === 0) return;

      const itemText = item.text.toLowerCase().trim();
      if (itemText && originalQuery.includes(itemText)) {
        replacements.push({
          original: itemText,
          options: validOptions,
        });
      }
    });

    if (replacements.length > 0) {
      let primarySuggestion = originalQuery;
      let hasValidReplacement = false;

      replacements.forEach((replacement) => {
        if (replacement.options.length > 0 && replacement.options[0]) {
          const parts = primarySuggestion.split(replacement.original);
          if (parts.length > 1) {
            primarySuggestion = parts.join(replacement.options[0]);
            hasValidReplacement = true;
          }
        }
      });

      if (
        hasValidReplacement &&
        primarySuggestion.trim() &&
        primarySuggestion !== originalQuery
      ) {
        suggestions.push(primarySuggestion.trim());
      }

      if (replacements.length === 1 && suggestions.length < MAX_SUGGESTIONS) {
        const replacement = replacements[0];
        const alternativeOptions = replacement.options.slice(1, 4);

        alternativeOptions.forEach((option) => {
          if (suggestions.length >= MAX_SUGGESTIONS) return;

          if (option && typeof option === "string" && option.trim()) {
            const parts = originalQuery.split(replacement.original);
            if (parts.length > 1) {
              const suggestion = parts.join(option).trim();
              if (
                suggestion &&
                suggestion !== originalQuery &&
                !suggestions.includes(suggestion)
              ) {
                suggestions.push(suggestion);
              }
            }
          }
        });
      }
    }

    const validSuggestions = suggestions
      .filter(
        (suggestion) =>
          suggestion &&
          typeof suggestion === "string" &&
          suggestion.trim() !== "" &&
          suggestion.toLowerCase() !== "undefined" &&
          suggestion !== originalQuery &&
          suggestion.length <= MAX_QUERY_LENGTH,
      )
      .slice(0, MAX_SUGGESTIONS);

    return [...new Set(validSuggestions)];
  };

  const suggestions = generateSuggestions();

  const handleQueryChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleSearchFieldChange = (field: SearchFieldKey, checked: boolean) => {
    setSearchFields((prev) => {
      const newFields = { ...prev, [field]: checked };
      return newFields;
    });
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleQueryChange(suggestion);
  };

  return (
    <Box sx={{ py: { xs: 1.25, md: 1.5 }, ...hairline }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ width: "100%", maxWidth: { xs: "100%", md: "42rem" } }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search articles by title, author, keyword or DOI"
            value={searchQuery}
            onChange={(e) => handleQueryChange(e.target.value)}
            onKeyPress={handleKeyPress}
            slotProps={{
              htmlInput: { "aria-label": "Search query" },
              input: {
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <Button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      sx={{
                        minWidth: 0,
                        px: 0.5,
                        borderRadius: 0,
                        fontFamily: FONT_UI,
                        fontSize: "0.6875rem",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "custom.inkMuted",
                        "&:hover": {
                          backgroundColor: "transparent",
                          color: "primary.main",
                        },
                      }}
                    >
                      Clear
                    </Button>
                  </InputAdornment>
                ) : null,
              },
            }}
            sx={{
              ...fieldSx,
              "& .MuiOutlinedInput-root": {
                ...fieldSx["& .MuiOutlinedInput-root"],
                fontFamily: FONT_TEXT,
                fontSize: { xs: "1rem", md: "1.1875rem" },
                minHeight: { xs: "3rem", md: "3.5rem" },
              },
              "& .MuiOutlinedInput-input": {
                paddingBlock: { xs: "0.75rem", md: "0.9375rem" },
              },
            }}
          />
        </Box>

        {suggestions.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              flexWrap: "wrap",
              gap: 0.5,
              fontFamily: FONT_UI,
              fontSize: "0.8125rem",
            }}
          >
            <Box component="span" sx={{ color: "custom.inkMuted" }}>
              Did you mean
            </Box>
            {suggestions.map((suggestion, index) => (
              <Box
                key={index}
                component="button"
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                sx={{
                  border: 0,
                  p: 0,
                  background: "none",
                  cursor: "pointer",
                  fontFamily: FONT_TEXT,
                  fontSize: "0.875rem",
                  fontStyle: "italic",
                  color: "primary.main",
                  textDecoration: "underline",
                }}
              >
                {suggestion}
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            columnGap: 0.75,
            rowGap: 0.375,
          }}
        >
          <Kicker component="span">Search in</Kicker>
          <FormGroup
            row
            sx={{ flexWrap: "wrap", alignItems: "center", gap: 0.375 }}
          >
            {SEARCH_FIELDS.map(({ key, label }) => (
              <Box key={key} component="label" sx={pillSx(searchFields[key])}>
                <input
                  type="checkbox"
                  checked={searchFields[key]}
                  onChange={(e) =>
                    handleSearchFieldChange(key, e.target.checked)
                  }
                  style={VISUALLY_HIDDEN}
                />
                {label}
              </Box>
            ))}
          </FormGroup>
        </Box>
      </Box>
    </Box>
  );
};

const SearchBar = dynamic(() => Promise.resolve(SearchBarMain), { ssr: false });

export default SearchBar;
